import { createTheme } from '@mui/material';

// Figma-inspired theme
// Based on Figma's minimalist UI approach with neutral colors,
// clean typography, and subtle shadows

const figmaTheme = createTheme({
  palette: {
    primary: {
      main: '#0d99ff', // Figma blue
      light: '#4db8ff',
      dark: '#007acc',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6e6e6e', // Neutral gray for secondary actions
      light: '#9e9e9e',
      dark: '#4a4a4a',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f24822', // Error red
      light: '#ff7a5c',
      dark: '#c41c00',
    },
    warning: {
      main: '#ffab00', // Warning orange/amber
      light: '#ffdd4b',
      dark: '#c67c00',
    },
    success: {
      main: '#0ca789', // Success green
      light: '#5ed9bb',
      dark: '#00775a',
    },
    info: {
      main: '#2196f3', // Info blue
      light: '#64b5f6',
      dark: '#0b79d0',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c2c2c',
      secondary: '#6e6e6e',
      disabled: '#c7c7c7',
    },
    divider: '#e6e6e6',
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.25,
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.05em',
      lineHeight: 1.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 6, // Figma typically uses subtle rounded corners
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.06)', // Subtle shadow for cards and buttons
    '0px 2px 4px rgba(0, 0, 0, 0.06)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 6px 10px rgba(0, 0, 0, 0.08)',
    '0px 8px 12px rgba(0, 0, 0, 0.08)',
    '0px 10px 14px rgba(0, 0, 0, 0.10)',
    '0px 12px 16px rgba(0, 0, 0, 0.10)',
    '0px 14px 18px rgba(0, 0, 0, 0.10)',
    '0px 16px 20px rgba(0, 0, 0, 0.10)',
    '0px 18px 22px rgba(0, 0, 0, 0.10)',
    '0px 20px 24px rgba(0, 0, 0, 0.12)',
    '0px 22px 26px rgba(0, 0, 0, 0.12)',
    '0px 24px 28px rgba(0, 0, 0, 0.12)',
    '0px 26px 30px rgba(0, 0, 0, 0.12)',
    '0px 28px 32px rgba(0, 0, 0, 0.12)',
    '0px 30px 34px rgba(0, 0, 0, 0.12)',
    '0px 32px 36px rgba(0, 0, 0, 0.12)',
    '0px 34px 38px rgba(0, 0, 0, 0.12)',
    '0px 36px 40px rgba(0, 0, 0, 0.12)',
    '0px 38px 42px rgba(0, 0, 0, 0.12)',
    '0px 40px 44px rgba(0, 0, 0, 0.12)',
    '0px 42px 46px rgba(0, 0, 0, 0.12)',
    '0px 44px 48px rgba(0, 0, 0, 0.12)',
  ],
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#2c2c2c',
          borderBottom: '1px solid #e6e6e6',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.06)',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#007acc',
          },
        },
        outlinedPrimary: {
          borderColor: '#0d99ff',
          '&:hover': {
            backgroundColor: 'rgba(13, 153, 255, 0.04)',
          },
        },
        textPrimary: {
          '&:hover': {
            backgroundColor: 'rgba(13, 153, 255, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#9e9e9e',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0d99ff',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation4: {
          boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f5f5f5',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minWidth: 100,
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          height: 28,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          minHeight: 36,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: '#e6e6e6',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
  },
});

export default figmaTheme;